import { renderDateTimeView, renderUsersView } from '@/Ship/Helpers/fieldViews'

import type { IFieldConfig } from '@/Core/Contracts/UI/Form'

/**
 * Спільне для полів довідників трьох сімей (`SPEC-007` §7.5).
 *
 * Сімейні набори полів — у `{StatusList,LossReasonList,CrmList}Fields.ts`;
 * тут — те, що їм спільне: блок «Службова інформація» (G-02b) і правило
 * «код системного запису не змінюється» (R-04).
 */

/** Значення форми так, як їх бачить `depFn`. */
export interface DictionaryFormValues {
    uuid?: string
    isSystem?: boolean
    [key: string]: unknown
}

/** Мінімум поля й форми, з яким працює `depFn` ядра. */
interface DepFnArgs {
    field: {
        mergeInputProps?: (props: Record<string, unknown>) => void
    }
    formContext: {
        contextKey?: string
        values?: DictionaryFormValues
    }
}

const isNewRecord = (values: DictionaryFormValues | undefined): boolean =>
    typeof values?.uuid !== 'string' || values.uuid === ''

/**
 * «Код» системного запису вимкнений (R-04, ADR 024): на нього спирається
 * логіка. Новий і звичайний запис код редагують — у легасі поле статусу було
 * вимкнене завжди, і створити статус з інтерфейсу було неможливо (D-30).
 *
 * Бек тримає те саме правило сам (422 «Код системного запису змінювати не
 * можна.»), тут — лише щоб не пропонувати правку, яку він відхилить.
 *
 * Повертає `undefined`, а не `false`: `false` з `depFn` ядро читає як
 * «сховати поле». Перевикликається на кожну зміну значень форми, тож
 * вмикаємо й вимикаємо в обидва боки.
 */
export const lockCodeOfSystemRow = ({ field, formContext }: DepFnArgs): void => {
    if (formContext.contextKey === 'filterFormContext') return

    field.mergeInputProps?.({ disabled: formContext.values?.isSystem === true })
}

/**
 * Службове поле показується лише в картці збереженого запису: у формі
 * створення воно порожнє за визначенням.
 */
const onlySaved = ({ formContext }: DepFnArgs): boolean | undefined => {
    if (formContext.contextKey === 'filterFormContext') return undefined

    return isNewRecord(formContext.values) ? false : undefined
}

/**
 * «Службова інформація» (G-02b): ідентифікатор і аудит, лише читання.
 * Підписи — дослівно з легасі (`Id` → «Ід»).
 *
 * ⚠️ Фабрика, а не спільний об'єкт: ядро дописує в описи полів власні
 * властивості (`inputProps`, підказки фільтра), і спільний екземпляр
 * протікав би з одного довідника в інший.
 */
export const serviceFields = (): Record<string, IFieldConfig> => ({
    // Без `inputProps.disabled`: «Ід» — ще й фільтр списку, а фільтр ядро
    // будує з того самого опису поля
    id: {
        label: 'Ід',
        type: 'number',
        isEditable: false,
        depFn: onlySaved,
    },
    createdAt: {
        label: 'Дата та час створення',
        type: 'datetime',
        isEditable: false,
        inputProps: { disabled: true },
        view: renderDateTimeView,
        depFn: onlySaved,
    },
    createdBy: {
        label: 'Ким створено',
        type: 'select',
        resource: 'Setting.User',
        isEditable: false,
        inputProps: { disabled: true },
        view: renderUsersView,
        depFn: onlySaved,
    },
    updatedAt: {
        label: 'Дата та час зміни',
        type: 'datetime',
        isEditable: false,
        inputProps: { disabled: true },
        view: renderDateTimeView,
        depFn: onlySaved,
    },
    updatedBy: {
        label: 'Ким змінено',
        type: 'select',
        resource: 'Setting.User',
        isEditable: false,
        inputProps: { disabled: true },
        view: renderUsersView,
        depFn: onlySaved,
    },
})

/** Пошук у селектах CRM-форм (легасі `_search`: назва й код, `SPEC-007` §6.4). */
export const DICTIONARY_SEARCH_FIELDS = ['id', 'code', 'title']
